import { RefObject, useCallback, useEffect, useRef } from 'react';

const KEY_NAME_ESC = 'Escape';
const KEY_EVENT_TYPE = 'keyup';

type Out<T extends HTMLElement> = {
  ref: RefObject<T>;
};

/**
 * Used for detection of pressing Escape button.
 * Can be attached globally to the document or preferable to a specific component
 *
 * @example
 * const { ref } = useEscapeKey<HTMLDivElement>(() => alert('hello'));
 *
 * <Box ref={ref}>
 *   ...
 * </Box>
 */

export const useEscapeKey = <T extends HTMLElement>(handleEsc?: () => void): Out<T> => {
  const ref = useRef<T>(null);

  const handleEscKey = useCallback(
    (event: { key: string }): void => {
      if (event.key === KEY_NAME_ESC && handleEsc) {
        handleEsc();
      }
    },
    [handleEsc],
  );

  useEffect(() => {
    const current = ref.current;

    if (handleEsc) {
      if (current) {
        current.addEventListener(KEY_EVENT_TYPE, handleEscKey, false);
        document.removeEventListener(KEY_EVENT_TYPE, handleEscKey, false);
      } else {
        document.addEventListener(KEY_EVENT_TYPE, handleEscKey, false);
      }
    }

    return (): void => {
      if (handleEsc) {
        if (current) {
          current.removeEventListener(KEY_EVENT_TYPE, handleEscKey, false);
        }
        document.removeEventListener(KEY_EVENT_TYPE, handleEscKey, false);
      }
    };
  }, [ref, handleEscKey, handleEsc]);

  return {
    ref,
  };
};
